import { useState, useEffect } from 'react';
import { X, Loader, ChevronLeft, ChevronRight, Save } from 'lucide-react';
import type { SavedQuery, CreateQueryRequest } from '../../types/query';
import type { QueryFormData } from '../../types/queryForm';
import { defaultQueryFormData } from '../../types/queryForm';
import StepNavigation from './wizard/StepNavigation';
import Step1Definitions from './wizard/Step1Definitions';
import Step2Configuration from './wizard/Step2Configuration';
import Step3Availability from './wizard/Step3Availability';

interface QueryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateQueryRequest) => Promise<void>;
  query?: SavedQuery;
  isSubmitting: boolean;
}

const STEPS = [
  { id: 1, name: 'Definições', description: 'Nome e Query SQL', required: true },
  { id: 2, name: 'Configurações', description: 'Campos e filtros', required: false },
  { id: 3, name: 'Disponibilização', description: 'API ou Integração', required: false }
];

export default function QueryModal({ isOpen, onClose, onSave, query, isSubmitting }: QueryModalProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<QueryFormData>(defaultQueryFormData);
  const [availableColumns, setAvailableColumns] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      if (query) {
        setFormData({
          name: query.name,
          description: query.description || '',
          sqlQuery: query.sql_query,
          automationInterval: query.automation_interval || '24h',
          columnDistributor: query.column_distributor || '',
          columnIndustry: query.column_industry || '',
          selectedColumns: query.selected_columns || [],
          filterColumns: query.filter_columns || [],
          documentationDescription: query.documentation_description || '',
          availabilityType: query.availability_type || 'api',
          endpointSlug: query.endpoint_slug,
          isActive: query.is_active,
          solaceConfig: {
            enabled: query.integration_solace_enabled || false,
            brokerUrl: query.integration_solace_broker_url || '',
            vpnName: query.integration_solace_vpn_name || '',
            queueName: query.integration_solace_queue_name || '',
            username: query.integration_solace_username || '',
            password: query.integration_solace_password || '',
            interval: query.integration_solace_interval || '24h'
          },
          sapBtpConfig: {
            enabled: query.integration_sap_btp_enabled || false,
            serviceUrl: query.integration_sap_btp_service_url || '',
            clientId: query.integration_sap_btp_client_id || '',
            clientSecret: query.integration_sap_btp_client_secret || '',
            tokenUrl: query.integration_sap_btp_token_url || '',
            apiPath: query.integration_sap_btp_api_path || '',
            interval: query.integration_sap_btp_interval || '24h'
          }
        });
        const cols = new Set<string>(query.selected_columns || []);
        if (query.column_distributor) cols.add(query.column_distributor);
        if (query.column_industry) cols.add(query.column_industry);
        if (cols.size > 0) {
          setAvailableColumns(Array.from(cols));
        }
      } else {
        setFormData(defaultQueryFormData);
        setAvailableColumns([]);
      }
      setCurrentStep(1);
    }
  }, [isOpen, query]);

  const handleFormChange = (data: Partial<QueryFormData>) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  const handleColumnsDiscovered = (columns: string[]) => {
    setAvailableColumns(columns);
  };

  const isStep1Valid = (): boolean => {
    if (!formData.name.trim() || !formData.sqlQuery.trim()) return false;
    if (!formData.columnDistributor || !formData.columnIndustry) return false;
    return true;
  };

  const isStep3Valid = (): boolean => {
    const isApiEnabled = formData.availabilityType === 'api' || formData.availabilityType === 'both';
    if (isApiEnabled && !formData.endpointSlug.trim()) return false;
    return true;
  };

  const canNavigateToStep = (step: number): boolean => {
    if (step === 1) return true;
    if (step === 2) return isStep1Valid();
    if (step === 3) return isStep1Valid();
    return false;
  };

  const handleStepClick = (step: number) => {
    if (canNavigateToStep(step)) {
      setCurrentStep(step);
    }
  };

  const handleNext = () => {
    if (currentStep < 3 && canNavigateToStep(currentStep + 1)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canSubmit = (): boolean => {
    return isStep1Valid() && isStep3Valid();
  };

  const handleSubmit = async () => {
    if (!canSubmit()) return;

    const isApiEnabled = formData.availabilityType === 'api' || formData.availabilityType === 'both';
    const isIntegrationEnabled = formData.availabilityType === 'integration' || formData.availabilityType === 'both';

    await onSave({
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      sql_query: formData.sqlQuery.trim(),
      endpoint_slug: isApiEnabled ? formData.endpointSlug.trim() : formData.endpointSlug.trim() || formData.name.toLowerCase().replace(/\s+/g, '-'),
      selected_columns: formData.selectedColumns,
      filter_columns: formData.filterColumns,
      column_distributor: formData.columnDistributor,
      column_industry: formData.columnIndustry,
      availability_type: formData.availabilityType,
      documentation_description: formData.documentationDescription.trim() || undefined,
      is_active: formData.isActive,
      automation_enabled: true,
      automation_interval: formData.automationInterval,
      integration_solace_enabled: isIntegrationEnabled && formData.solaceConfig.enabled,
      integration_solace_broker_url: isIntegrationEnabled && formData.solaceConfig.enabled ? formData.solaceConfig.brokerUrl : undefined,
      integration_solace_vpn_name: isIntegrationEnabled && formData.solaceConfig.enabled ? formData.solaceConfig.vpnName : undefined,
      integration_solace_queue_name: isIntegrationEnabled && formData.solaceConfig.enabled ? formData.solaceConfig.queueName : undefined,
      integration_solace_username: isIntegrationEnabled && formData.solaceConfig.enabled ? formData.solaceConfig.username : undefined,
      integration_solace_password: isIntegrationEnabled && formData.solaceConfig.enabled ? formData.solaceConfig.password : undefined,
      integration_solace_interval: isIntegrationEnabled && formData.solaceConfig.enabled ? formData.solaceConfig.interval : undefined,
      integration_sap_btp_enabled: isIntegrationEnabled && formData.sapBtpConfig.enabled,
      integration_sap_btp_service_url: isIntegrationEnabled && formData.sapBtpConfig.enabled ? formData.sapBtpConfig.serviceUrl : undefined,
      integration_sap_btp_client_id: isIntegrationEnabled && formData.sapBtpConfig.enabled ? formData.sapBtpConfig.clientId : undefined,
      integration_sap_btp_client_secret: isIntegrationEnabled && formData.sapBtpConfig.enabled ? formData.sapBtpConfig.clientSecret : undefined,
      integration_sap_btp_token_url: isIntegrationEnabled && formData.sapBtpConfig.enabled ? formData.sapBtpConfig.tokenUrl : undefined,
      integration_sap_btp_api_path: isIntegrationEnabled && formData.sapBtpConfig.enabled ? formData.sapBtpConfig.apiPath : undefined,
      integration_sap_btp_interval: isIntegrationEnabled && formData.sapBtpConfig.enabled ? formData.sapBtpConfig.interval : undefined
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {query ? 'Editar Serviço' : 'Novo Serviço'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <StepNavigation
            steps={STEPS}
            currentStep={currentStep}
            onStepClick={handleStepClick}
            canNavigate={canNavigateToStep}
          />
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {currentStep === 1 && (
            <Step1Definitions
              formData={formData}
              onChange={handleFormChange}
              onColumnsDiscovered={handleColumnsDiscovered}
              isEditing={!!query}
              availableColumns={availableColumns}
            />
          )}
          {currentStep === 2 && (
            <Step2Configuration
              formData={formData}
              onChange={handleFormChange}
              availableColumns={availableColumns}
            />
          )}
          {currentStep === 3 && (
            <Step3Availability
              formData={formData}
              onChange={handleFormChange}
              availableColumns={availableColumns}
            />
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div>
            {currentStep > 1 && (
              <button
                type="button"
                onClick={handlePrevious}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Anterior
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>

            {currentStep < 3 ? (
              <button
                type="button"
                onClick={handleNext}
                disabled={!canNavigateToStep(currentStep + 1)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors"
              >
                Próximo
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting || !canSubmit()}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {query ? 'Salvar Alterações' : 'Criar Serviço'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
